import React from 'react'
import { styled, CSS, forwardRef, FloraVariants } from '@grupoboticario/flora-react-system'
import { filterUndefined, HTMLFloraProps } from '@grupoboticario/flora-react-utils'

const StyledImage = styled('img', {
  verticalAlign: 'middle',
  maxWidth: '100%',
  height: 'auto',
})

export type ImageProps = HTMLFloraProps<'img'> &
  FloraVariants<typeof StyledImage> & {
    /**
     * Defines width and height
     */
    size?: CSS['width']
    /**
     * Make sure pass alt attribute
     */
    alt: string
    /**
     * Defines loading strategy
     * lazy: Defer loading of the resource until it reaches a calculated distance from the viewport.
     * eager: Load the resource immediately, regardless of where it's located on the page.
     * @description: https://web.dev/browser-level-image-lazy-loading/
     */
    loading?: 'lazy' | 'eager'
    /**
     * How the image to fit within its bounds.
     * It maps to css `object-fit` property.
     * @type CSS["objectFit"]
     */
    fit?: CSS['objectFit']
    /**
     * How to align the image within its bounds.
     * It maps to css `object-position` property.
     * @type CSS["objectPosition"]
     */
    position?: CSS['objectPosition']
    /**
     * Shape of image
     * It maps to border-radius tokens
     */
    radius?: CSS['borderRadius']
  }

/**
 * @component
 * @name Image
 * @description Image component
 * @tutorial https://flora.gb.tech/docs/components/core/image
 */
export const Image = forwardRef<'img', ImageProps>(
  ({ size, fit, position, radius, ...props }, forwardedRef) => {
    const css = filterUndefined({
      size: !!isNaN(Number(size)) ? size : Number(size),
      objectFit: fit,
      objectPosition: position,
      borderRadius: radius,
      ...(props.css as any),
    })

    return <StyledImage {...props} data-flora="image" ref={forwardedRef} css={css} />
  },
)
